import { shiftIsoDate } from "./agendaDateFormat";
import {
  getFortalezaTodayIso,
  normalizeAgendaIsoDate,
} from "./agendaFiltersQuery";
import type { AgendaWeekDaySummary, AgendaWeekSummary } from "./agendaTypes";

/** Days from today onward shown in the strip (today included). */
export const AGENDA_WEEK_STRIP_DAY_COUNT = 15;
/** Past days the agenda can open (yesterday). The page still opens on today. */
export const AGENDA_PAST_DAY_COUNT = 1;

const WEEKDAY_SHORT = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];

function isoWeekdayShortInFortaleza(isoDate: string): string {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Fortaleza",
    weekday: "short",
  }).format(new Date(`${isoDate}T12:00:00-03:00`));

  const map: Record<string, string> = {
    Sun: "DOM",
    Mon: "SEG",
    Tue: "TER",
    Wed: "QUA",
    Thu: "QUI",
    Fri: "SEX",
    Sat: "SÁB",
  };

  return map[weekday] ?? WEEKDAY_SHORT[new Date(`${isoDate}T12:00:00-03:00`).getUTCDay()] ?? "—";
}

export function getAgendaWeekStripIsoDates(
  todayIso: string,
  dayCount = AGENDA_WEEK_STRIP_DAY_COUNT,
  pastDayCount = AGENDA_PAST_DAY_COUNT
): string[] {
  const normalizedToday = normalizeAgendaIsoDate(todayIso) ?? todayIso;
  return Array.from({ length: pastDayCount + dayCount }, (_, index) =>
    shiftIsoDate(normalizedToday, index - pastDayCount)
  );
}

export function getAgendaWeekStripStartIso(
  todayIso: string,
  pastDayCount = AGENDA_PAST_DAY_COUNT
): string {
  const normalizedToday = normalizeAgendaIsoDate(todayIso) ?? todayIso;
  return shiftIsoDate(normalizedToday, -pastDayCount);
}

export function getAgendaWeekStripEndIso(
  todayIso: string,
  dayCount = AGENDA_WEEK_STRIP_DAY_COUNT
): string {
  const dates = getAgendaWeekStripIsoDates(todayIso, dayCount);
  return dates[dates.length - 1] ?? todayIso;
}

export function clampAgendaScheduleDate(
  dateIso: string,
  todayIso: string = getFortalezaTodayIso(),
  dayCount = AGENDA_WEEK_STRIP_DAY_COUNT
): string {
  const normalized =
    normalizeAgendaIsoDate(dateIso) ?? normalizeAgendaIsoDate(todayIso) ?? todayIso;
  const normalizedToday = normalizeAgendaIsoDate(todayIso) ?? todayIso;
  const firstAllowed = getAgendaWeekStripStartIso(normalizedToday);
  const lastAllowed = getAgendaWeekStripEndIso(normalizedToday, dayCount);

  // Only yesterday is reachable in the past; older dates (stale links) open on today.
  if (normalized < firstAllowed) {
    return normalizedToday;
  }
  if (normalized > lastAllowed) {
    return lastAllowed;
  }
  return normalized;
}

export function buildAgendaWeekDayPlaceholder(isoDate: string): AgendaWeekDaySummary {
  const normalized = normalizeAgendaIsoDate(isoDate) ?? isoDate;
  const dayOfMonth = normalized.split("-")[2] ?? "01";

  return {
    date: normalized,
    weekday_label: isoWeekdayShortInFortaleza(normalized),
    day_of_month: dayOfMonth,
    visits_count: 0,
  };
}

/** Strip with dates only, used until the week counts arrive. */
export function buildPendingAgendaWeekSummary(
  todayIso: string = getFortalezaTodayIso()
): AgendaWeekSummary {
  return {
    days: getAgendaWeekStripIsoDates(todayIso).map((isoDate) => ({
      ...buildAgendaWeekDayPlaceholder(isoDate),
      pending: true,
    })),
  };
}

export function projectWeekSummaryToWeekStrip(
  summary: AgendaWeekSummary,
  todayIso: string = getFortalezaTodayIso(),
  dayCount = AGENDA_WEEK_STRIP_DAY_COUNT
): AgendaWeekSummary {
  const expectedDates = getAgendaWeekStripIsoDates(todayIso, dayCount);
  const byDate = new Map<string, AgendaWeekDaySummary>();

  summary.days.forEach((day) => {
    const key = normalizeAgendaIsoDate(day.date) ?? day.date;
    byDate.set(key, {
      ...day,
      date: key,
    });
  });

  return {
    days: expectedDates.map((isoDate) => {
      return byDate.get(isoDate) ?? buildAgendaWeekDayPlaceholder(isoDate);
    }),
  };
}

export function buildAgendaWeekSummaryParams(
  teamId?: number,
  todayIso: string = getFortalezaTodayIso()
): Record<string, string | number> {
  const normalizedToday = normalizeAgendaIsoDate(todayIso) ?? todayIso;
  const startDate = getAgendaWeekStripStartIso(normalizedToday);
  const endDate = getAgendaWeekStripEndIso(normalizedToday);

  const params: Record<string, string | number> = {
    date: startDate,
    start_date: startDate,
    end_date: endDate,
  };

  if (teamId != null) {
    params.team_id = teamId;
  }

  return params;
}
